import { useMemo, useRef } from 'react'
import { Printer } from 'lucide-react'
import { useLmsPrint } from '@/hooks/useLmsPrint'
import { LmsPdfDownloadButton } from '@/components/admin/print/LmsPdfDownloadButton'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { ChapterReportDocument } from '@/components/admin/chapter-report/ChapterReportDocument'
import { useChapterReport, useResolveChapterReport } from '@/hooks/coaching/useChapterReport'
import {
  formatPrintFileNameTimestamp,
  sanitizePrintFileNameSegment,
} from '@/utils/printFileName'

export const CHAPTER_REPORT_PRINT_PAGE_STYLE = `
  @page {
    size: A4 portrait;
    margin: 14mm 13mm 18mm;
    @bottom-center {
      content: counter(page);
      color: #333;
      font-family: sans-serif;
      font-size: 12px;
    }
  }
  @media print {
    html, body { background: #fff !important; print-color-adjust: exact; -webkit-print-color-adjust: exact; }
    .chapter-report-print-action { display: none !important; }
    .chapter-report-print-shell { padding: 0 !important; background: #fff !important; }
    .chapter-report-document { width: auto !important; max-width: none !important; min-height: 0; padding: 0 !important; box-shadow: none !important; }
    .chapter-report-print-block { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    .chapter-report-print-section, .chapter-report-row, .chapter-report-print-block, tr, article { break-inside: avoid; page-break-inside: avoid; }
    thead { display: table-header-group; }
  }
`

function parsePositiveInt(value: string | undefined): number | null {
  if (!value) return null
  const parsed = Number(value)
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null
}

export default function ChapterReportPrintPage() {
  const { t } = useTranslation('coaching')
  const { learnerId: learnerIdRaw, testId: testIdRaw } = useParams<{
    learnerId: string
    testId: string
  }>()
  const learnerId = parsePositiveInt(learnerIdRaw)
  const testId = parsePositiveInt(testIdRaw)

  if (learnerId == null || testId == null) {
    return <div className="p-8 text-red-600" role="alert">{t('chapterReport.invalidParams')}</div>
  }
  return <ChapterReportPrintContent learnerId={learnerId} testId={testId} />
}

function ChapterReportPrintContent({
  learnerId,
  testId,
}: {
  learnerId: number
  testId: number
}) {
  const { t } = useTranslation('coaching')
  const printRef = useRef<HTMLDivElement>(null)
  const resolveQuery = useResolveChapterReport(learnerId, testId)
  const reportQuery = useChapterReport(learnerId, resolveQuery.data?.reportId)
  const report = reportQuery.data
  const documentTitle = useMemo(() => {
    if (!report) return 'chapter-report'
    return sanitizePrintFileNameSegment(t('chapterReport.documentTitle', {
      name: report.learner.fullName,
      loginId: report.learner.loginId,
      chapter: report.chapter.title,
      timestamp: formatPrintFileNameTimestamp(new Date(report.latestTest.generatedAt)),
    }), 'chapter-report')
  }, [report, t])
  const canPrint = !!report && !resolveQuery.isFetching && !reportQuery.isFetching && !resolveQuery.error && !reportQuery.error
  const { print, downloadPdf, canDownloadPdf, isDownloading, error: pdfError } = useLmsPrint({
    contentRef: printRef,
    canPrint,
    documentTitle,
    pageStyle: CHAPTER_REPORT_PRINT_PAGE_STYLE,
  })

  if (resolveQuery.isLoading || reportQuery.isLoading) {
    return <div className="p-8" role="status" aria-live="polite">{t('chapterReport.loading')}</div>
  }
  if (resolveQuery.error || reportQuery.error || !report) {
    return <div className="p-8 text-red-600" role="alert">{t('chapterReport.loadError')}</div>
  }

  return (
    <main aria-label={t('chapterReport.printPageLabel')} className="min-h-screen bg-[#f3f5f8]">
      <div className="chapter-report-print-action sticky top-0 z-20 border-b border-gray-200 bg-white px-6 py-3">
        <div className="mx-auto flex max-w-[850px] justify-end gap-2">
          <button
            type="button"
            onClick={() => print()}
            className="inline-flex items-center gap-2 rounded-lg bg-[#4599ff] px-4 py-2 text-sm font-semibold text-white"
          >
            <Printer size={16} aria-hidden="true" />
            {t('chapterReport.print')}
          </button>
          {canDownloadPdf && (
            <LmsPdfDownloadButton onClick={downloadPdf} disabled={!canPrint} isDownloading={isDownloading} error={pdfError} />
          )}
        </div>
      </div>
      <div className="chapter-report-print-shell mx-auto py-8">
        <div ref={printRef} className="mx-auto max-w-[794px] shadow-lg print:shadow-none">
          <ChapterReportDocument
            report={report}
            selectedLessonIds={report.recommendedLessonIds}
            overallComment={report.overallComment ?? ''}
          />
        </div>
      </div>
    </main>
  )
}
